import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { authApi, errorMessage, unwrap } from "@/api/client";
import { Alert, Field, PasswordField } from "@/ui";

export function ForgotPage() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const schema = z.object({ email: z.string().email("Введите email") });
  const { register, handleSubmit, formState } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });
  const onSubmit = async (f: { email: string }) => {
    setError(null);
    try {
      unwrap(await authApi.POST("/api/v1/auth/forgot-password", { body: f }));
      nav("/reset-sent", { state: { email: f.email } });
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <form className="auth-card" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div>
        <h1>Восстановить пароль</h1>
        <p className="auth-card__sub">Пришлём ссылку для смены пароля на вашу почту.</p>
      </div>
      {error && <Alert kind="error">{error}</Alert>}
      <Field
        label="Email"
        type="email"
        placeholder="you@example.com"
        autoComplete="email"
        error={formState.errors.email?.message}
        {...register("email")}
      />
      <button className="btn btn--block" type="submit" disabled={formState.isSubmitting}>
        Отправить ссылку
      </button>
      <Link to="/login" className="small">
        Вернуться ко входу
      </Link>
    </form>
  );
}

export function ResetSentPage() {
  const email = (useLocation().state as { email?: string } | null)?.email;
  return (
    <div className="auth-card">
      <h1>Проверьте почту</h1>
      <p className="muted" style={{ margin: 0 }}>
        Если адрес {email ?? "указанный"} зарегистрирован, мы отправили на него ссылку для смены
        пароля. Ссылка действует ограниченное время.
      </p>
      <Link className="btn btn--block" to="/login">
        Вернуться ко входу
      </Link>
    </div>
  );
}

const resetSchema = z
  .object({ password: z.string().min(8, "Минимум 8 символов"), confirm: z.string() })
  .refine((f) => f.password === f.confirm, { path: ["confirm"], message: "Пароли не совпадают" });

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
  });
  const onSubmit = async (f: { password: string }) => {
    setError(null);
    try {
      const r = unwrap(
        await authApi.POST("/api/v1/auth/reset-password", {
          body: { token, password: f.password },
        }),
      );
      nav("/login", { state: { message: r.message } });
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  if (!token)
    return (
      <div className="auth-card">
        <h1>Ссылка неполная</h1>
        <Alert kind="error">В ссылке нет токена. Запросите восстановление пароля ещё раз.</Alert>
        <Link className="btn btn--block" to="/forgot">
          Запросить снова
        </Link>
      </div>
    );
  return (
    <form className="auth-card" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div>
        <h1>Новый пароль</h1>
        <p className="auth-card__sub">После смены все другие сессии будут завершены.</p>
      </div>
      {error && <Alert kind="error">{error}</Alert>}
      <PasswordField
        label="Новый пароль"
        id="password"
        autoComplete="new-password"
        error={formState.errors.password?.message}
        {...register("password")}
      />
      <PasswordField
        label="Повторите пароль"
        id="confirm"
        autoComplete="new-password"
        error={formState.errors.confirm?.message}
        {...register("confirm")}
      />
      <button className="btn btn--block" type="submit" disabled={formState.isSubmitting}>
        Сохранить пароль
      </button>
    </form>
  );
}

const changeSchema = z
  .object({
    current_password: z.string().min(1, "Введите текущий пароль"),
    new_password: z.string().min(8, "Минимум 8 символов"),
    confirm: z.string(),
    logout_others: z.boolean(),
  })
  .refine((f) => f.new_password === f.confirm, {
    path: ["confirm"],
    message: "Пароли не совпадают",
  });

export function ChangePasswordPage() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<z.infer<typeof changeSchema>>({
    resolver: zodResolver(changeSchema),
    defaultValues: { logout_others: true },
  });
  const onSubmit = async (f: z.infer<typeof changeSchema>) => {
    setError(null);
    try {
      unwrap(
        await authApi.POST("/api/v1/auth/change-password", {
          body: {
            current_password: f.current_password,
            new_password: f.new_password,
            logout_others: f.logout_others,
          },
        }),
      );
      nav("/profile", { state: { message: "Пароль изменён" } });
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <main className="main main--narrow">
      <form
        className="auth-card"
        style={{ maxWidth: "none" }}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div>
          <h1>Поменять пароль</h1>
          <p className="auth-card__sub">Введите текущий пароль и новый.</p>
        </div>
        {error && <Alert kind="error">{error}</Alert>}
        <PasswordField
          label="Текущий пароль"
          id="current"
          autoComplete="current-password"
          error={formState.errors.current_password?.message}
          {...register("current_password")}
        />
        <PasswordField
          label="Новый пароль"
          id="new"
          autoComplete="new-password"
          hint="Минимум 8 символов"
          error={formState.errors.new_password?.message}
          {...register("new_password")}
        />
        <PasswordField
          label="Повторите новый пароль"
          id="confirm"
          autoComplete="new-password"
          error={formState.errors.confirm?.message}
          {...register("confirm")}
        />
        <label className="checkbox">
          <input type="checkbox" {...register("logout_others")} />
          <span>Выйти на всех других устройствах</span>
        </label>
        <div className="row">
          <button className="btn" type="submit" disabled={formState.isSubmitting}>
            Сохранить
          </button>
          <Link to="/profile" className="btn btn--secondary">
            Отмена
          </Link>
        </div>
      </form>
    </main>
  );
}
